import React, { ComponentProps, MouseEventHandler, ReactNode } from "react";
import styled, { keyframes } from "styled-components";
import { Color, FontSize, Radius, Transition } from "@glossy";
import * as Tooltip from "@radix-ui/react-tooltip";

export type Placement = ComponentProps<typeof Tooltip.Content>["side"];

interface IProps {
  children: any;
  tooltip: ReactNode;
  placement?: Placement;
  disabled?: boolean;
  tiny?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onMouseDown?: MouseEventHandler<HTMLButtonElement>;
}

const translateDistance = "5px";

const slideUpAndFade = keyframes({
  "0%": { opacity: 0, transform: `translateY(${translateDistance})` },
  "100%": { opacity: 1, transform: "translateY(0)" },
});

const slideRightAndFade = keyframes({
  "0%": { opacity: 0, transform: `translateX(-${translateDistance})` },
  "100%": { opacity: 1, transform: "translateX(0)" },
});

const slideDownAndFade = keyframes({
  "0%": { opacity: 0, transform: `translateY(-${translateDistance})` },
  "100%": { opacity: 1, transform: "translateY(0)" },
});

const slideLeftAndFade = keyframes({
  "0%": { opacity: 0, transform: `translateX(${translateDistance})` },
  "100%": { opacity: 1, transform: "translateX(0)" },
});

const TooltipContent = styled(Tooltip.Content)<{ $tiny?: boolean }>`
  background-color: ${Color.BlackPearl};
  color: ${Color.White};
  padding: ${(props) => (props.$tiny ? "6px 12px" : "16px")};
  font-size: ${(props) => (props.$tiny ? FontSize.Small : FontSize.Medium)};
  border-radius: ${Radius.MediumLarge};

  @media (prefers-reduced-motion: no-preference) {
    animation-duration: ${Transition.Length};
    animation-timing-function: ease-in-out;
    will-change: transform, opacity;

    &[data-state="delayed-open"] {
      &[data-side="top"] {
        animation-name: ${slideDownAndFade};
      }

      &[data-side="right"] {
        animation-name: ${slideLeftAndFade};
      }

      &[data-side="bottom"] {
        animation-name: ${slideUpAndFade};
      }

      &[data-side="left"] {
        animation-name: ${slideRightAndFade};
      }
    }
  }
`;

const StyledArrow = styled(Tooltip.Arrow)`
  fill: ${Color.BlackPearl};
`;

const UiKitTooltip = ({
  children,
  tooltip,
  placement = "top",
  disabled = false,
  tiny = false,
  onClick,
  onMouseDown,
}: IProps) => (
  <Tooltip.Root delayDuration={0}>
    <Tooltip.Trigger asChild onClick={onClick} onMouseDown={onMouseDown}>
      {children}
    </Tooltip.Trigger>
    {!disabled && (
      <TooltipContent sideOffset={6} $tiny={tiny} side={placement}>
        {tooltip}
        {!tiny && <StyledArrow offset={12} />}
      </TooltipContent>
    )}
  </Tooltip.Root>
);

export default UiKitTooltip;
